import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IPartnerAuditEvent,
  IPartnerClient,
  IPartnerClientCreated,
  IPartnerToken,
  PartnerService,
} from '../../services/partner.service';
import { UserService } from '../../services/user.service';
import { useAppSelector } from '../../store/hooks';
import { formatBackendDateTime } from '../../utils/date.utils';
import ConfirmationModal from '../Modal/ConfirmationModal';
import CreateCredentialModal from './CreateCredentialModal';
import SecretRevealModal from './SecretRevealModal';
import './autoparkApiTokens.css';

interface AutoparkApiTokensProps {
  autoparkId: string;
}

const AUDIT_LIMIT = 100;

// Что показать вместо отсутствующего момента (ещё не использован, не отозван)
const NO_TIMESTAMP = '—';

const ACTION_LABELS: Record<string, string> = {
  'credential.created': 'Создание учётных данных',
  'credential.revoked': 'Отзыв учётных данных',
  'token.issued': 'Выдача токена',
  'token.revoked': 'Отзыв токена',
  'credentials.access': 'Доступ к разделу',
};

const OUTCOME_LABELS: Record<string, string> = {
  success: 'Успешно',
  denied: 'Отказано',
  error: 'Ошибка',
};

const DENIED_REASON_LABELS: Record<string, string> = {
  unknown_client: 'неизвестный client_id',
  client_revoked: 'учётные данные отозваны',
  bad_secret: 'неверный секрет',
  not_found: 'не найдено',
  forbidden: 'недостаточно прав',
};

type RevokeTarget = { kind: 'client'; item: IPartnerClient } | { kind: 'token'; item: IPartnerToken };

function AutoparkApiTokens({ autoparkId }: AutoparkApiTokensProps) {
  const user = useAppSelector((state) => state.user.user);

  const [clients, setClients] = useState<IPartnerClient[]>([]);
  const [tokens, setTokens] = useState<IPartnerToken[]>([]);
  const [audit, setAudit] = useState<IPartnerAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Held only while the reveal modal is open; cleared (not persisted anywhere) on close.
  const [created, setCreated] = useState<IPartnerClientCreated | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);
  const [revoking, setRevoking] = useState(false);

  // Best-effort actor name resolution for created_by / actor_profile_id (the audit carries ids only).
  const [profileNames, setProfileNames] = useState<Record<string, string>>({});
  const requestedProfilesRef = useRef<Set<string>>(new Set());

  const fetchAll = useCallback(async () => {
    if (!autoparkId) return;
    setError(null);
    try {
      const [clientsRes, tokensRes, auditRes] = await Promise.all([
        PartnerService.getClients(autoparkId),
        PartnerService.getTokens(autoparkId),
        PartnerService.getAudit(autoparkId, AUDIT_LIMIT),
      ]);
      setClients(clientsRes);
      setTokens(tokensRes);
      setAudit(auditRes);
    } catch (err: any) {
      setError(err.message || 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, [autoparkId]);

  useEffect(() => {
    setLoading(true);
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const ids = new Set<string>();
    clients.forEach((c) => c.created_by && ids.add(c.created_by));
    audit.forEach((e) => e.actor_profile_id && ids.add(e.actor_profile_id));
    if (user?.id) ids.delete(user.id);

    ids.forEach((id) => {
      if (requestedProfilesRef.current.has(id)) return;
      requestedProfilesRef.current.add(id);
      UserService.getProfileName(id)
        .then(({ displayName }) => {
          if (displayName) setProfileNames((prev) => ({ ...prev, [id]: displayName }));
        })
        .catch(() => {
          // Leave it unresolved; the raw id is shown instead.
        });
    });
  }, [clients, audit, user?.id]);

  const shortId = (id: string) => id.slice(0, 8);

  const profileLabel = (id: string | null): string => {
    if (!id) return '—';
    if (user?.id === id) {
      const own = [user.firstName, user.lastName].filter(Boolean).join(' ');
      return own ? `${own} (вы)` : `${shortId(id)} (вы)`;
    }
    return profileNames[id] ?? shortId(id);
  };

  const clientLabel = (uuid: string | null): string => {
    if (!uuid) return '—';
    const client = clients.find((c) => c.id === uuid);
    return client ? `${client.name} (${client.client_id})` : shortId(uuid);
  };

  const actorLabel = (e: IPartnerAuditEvent): string => {
    if (e.actor_profile_id) return profileLabel(e.actor_profile_id);
    if (e.actor_client_id) return clientLabel(e.actor_client_id);
    return '—';
  };

  const detailLabel = (e: IPartnerAuditEvent): string => {
    const parts: string[] = [];
    if (e.outcome === 'denied' && typeof e.detail?.reason === 'string') {
      parts.push(DENIED_REASON_LABELS[e.detail.reason] ?? e.detail.reason);
    }
    if (e.action === 'credential.created' && typeof e.detail?.name === 'string') {
      parts.push(`«${e.detail.name}»`);
    }
    return parts.join(', ');
  };

  const handleCreated = (result: IPartnerClientCreated) => {
    setIsCreateOpen(false);
    setCreated(result);
    fetchAll();
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setActionError(null);
    try {
      if (revokeTarget.kind === 'client') {
        await PartnerService.revokeClient(autoparkId, revokeTarget.item.id);
      } else {
        await PartnerService.revokeToken(autoparkId, revokeTarget.item.id);
      }
      setRevokeTarget(null);
      await fetchAll();
    } catch (err: any) {
      setActionError(err.message || 'Не удалось выполнить действие');
      setRevokeTarget(null);
    } finally {
      setRevoking(false);
    }
  };

  const now = Date.now();
  const isExpired = (t: IPartnerToken) => new Date(t.expires_at).getTime() < now;

  if (loading) {
    return (
      <div className="autopark-api-tokens">
        <div className="autopark-api-tokens__state">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="autopark-api-tokens">
        <div className="autopark-api-tokens__state autopark-api-tokens__state--error">
          <p>Ошибка: {error}</p>
          <button className="api-tokens-btn api-tokens-btn--primary" onClick={fetchAll}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="autopark-api-tokens">
      {actionError && (
        <div className="api-tokens-alert">
          <span>{actionError}</span>
          <button type="button" className="api-tokens-link-btn" onClick={() => setActionError(null)}>
            Скрыть
          </button>
        </div>
      )}

      {/* Credentials */}
      <section className="api-tokens-section">
        <div className="api-tokens-section__header">
          <div>
            <h2 className="api-tokens-section__title">Учётные данные партнёрского API</h2>
            <p className="api-tokens-section__subtitle">
              По client_id и client_secret партнёр получает токены доступа к данным этого автопарка.
            </p>
          </div>
          <button className="api-tokens-btn api-tokens-btn--primary" onClick={() => setIsCreateOpen(true)}>
            Создать учётные данные
          </button>
        </div>

        <div className="api-tokens-table api-tokens-table--clients">
          <div className="api-tokens-table__head">
            <span>Название</span>
            <span>client_id</span>
            <span>Создано</span>
            <span>Кем</span>
            <span>Статус</span>
            <span></span>
          </div>
          {clients.length === 0 ? (
            <div className="api-tokens-table__empty">Учётных данных пока нет</div>
          ) : (
            clients.map((c) => {
              const revoked = Boolean(c.revoked_at);
              return (
                <div key={c.id} className={`api-tokens-table__row ${revoked ? 'api-tokens-table__row--muted' : ''}`}>
                  <span className="api-tokens-table__name">{c.name}</span>
                  <code className="api-tokens-code">{c.client_id}</code>
                  <span>{formatBackendDateTime(c.created_at, NO_TIMESTAMP)}</span>
                  <span>{profileLabel(c.created_by)}</span>
                  <span>
                    <span
                      className={`api-tokens-status ${revoked ? 'api-tokens-status--revoked' : 'api-tokens-status--live'}`}
                    >
                      {revoked ? `Отозвано ${formatBackendDateTime(c.revoked_at, NO_TIMESTAMP)}` : 'Активно'}
                    </span>
                  </span>
                  <span className="api-tokens-table__actions">
                    {!revoked && (
                      <button
                        type="button"
                        className="api-tokens-link-btn api-tokens-link-btn--danger"
                        onClick={() => setRevokeTarget({ kind: 'client', item: c })}
                      >
                        Отозвать
                      </button>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Tokens */}
      <section className="api-tokens-section">
        <div className="api-tokens-section__header">
          <div>
            <h2 className="api-tokens-section__title">Выданные токены</h2>
            <p className="api-tokens-section__subtitle">
              Активные сессии партнёров. Отзыв токена немедленно завершает соответствующую сессию.
            </p>
          </div>
        </div>

        <div className="api-tokens-table api-tokens-table--tokens">
          <div className="api-tokens-table__head">
            <span>Учётные данные</span>
            <span>Выдан</span>
            <span>Истекает</span>
            <span>Последнее использование</span>
            <span></span>
          </div>
          {tokens.length === 0 ? (
            <div className="api-tokens-table__empty">Активных токенов нет</div>
          ) : (
            tokens.map((t) => {
              const expired = isExpired(t);
              const source = clients.find((c) => c.client_id === t.client_id);
              return (
                <div key={t.id} className={`api-tokens-table__row ${expired ? 'api-tokens-table__row--muted' : ''}`}>
                  <span>
                    {source && <span className="api-tokens-table__name">{source.name} </span>}
                    <code className="api-tokens-code">{t.client_id}</code>
                  </span>
                  <span>{formatBackendDateTime(t.issued_at, NO_TIMESTAMP)}</span>
                  <span>
                    {formatBackendDateTime(t.expires_at, NO_TIMESTAMP)}
                    {expired && <span className="api-tokens-status api-tokens-status--revoked">Истёк</span>}
                  </span>
                  <span>{formatBackendDateTime(t.last_used_at, NO_TIMESTAMP)}</span>
                  <span className="api-tokens-table__actions">
                    <button
                      type="button"
                      className="api-tokens-link-btn api-tokens-link-btn--danger"
                      onClick={() => setRevokeTarget({ kind: 'token', item: t })}
                    >
                      Отозвать
                    </button>
                  </span>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* History */}
      <section className="api-tokens-section">
        <div className="api-tokens-section__header">
          <div>
            <h2 className="api-tokens-section__title">История</h2>
            <p className="api-tokens-section__subtitle">
              Последние {AUDIT_LIMIT} событий. Серия отказов в выдаче токена — признак утёкшего или устаревшего секрета
              у партнёра.
            </p>
          </div>
        </div>

        <div className="api-tokens-table api-tokens-table--audit">
          <div className="api-tokens-table__head">
            <span>Время</span>
            <span>Кто</span>
            <span>Действие</span>
            <span>Результат</span>
            <span>Детали</span>
          </div>
          {audit.length === 0 ? (
            <div className="api-tokens-table__empty">Событий пока нет</div>
          ) : (
            audit.map((e) => (
              <div
                key={e.id}
                className={`api-tokens-table__row ${e.outcome === 'denied' ? 'api-tokens-table__row--denied' : ''} ${e.outcome === 'error' ? 'api-tokens-table__row--error' : ''}`}
              >
                <span>{formatBackendDateTime(e.occurred_at, NO_TIMESTAMP)}</span>
                <span>{actorLabel(e)}</span>
                <span>{ACTION_LABELS[e.action] ?? e.action}</span>
                <span>
                  <span className={`api-tokens-status api-tokens-status--${e.outcome}`}>
                    {OUTCOME_LABELS[e.outcome] ?? e.outcome}
                  </span>
                </span>
                <span className="api-tokens-table__detail">
                  {detailLabel(e)}
                  {e.ip && <span className="api-tokens-table__ip">{e.ip}</span>}
                </span>
              </div>
            ))
          )}
        </div>
      </section>

      <CreateCredentialModal
        isOpen={isCreateOpen}
        autoparkId={autoparkId}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleCreated}
      />

      <SecretRevealModal created={created} onClose={() => setCreated(null)} />

      <ConfirmationModal
        isOpen={revokeTarget !== null}
        title={
          revokeTarget?.kind === 'client' ? `Отозвать учётные данные «${revokeTarget.item.name}»?` : 'Отозвать токен?'
        }
        description={
          revokeTarget?.kind === 'client'
            ? 'Партнёр больше не сможет получать новые токены по этим учётным данным. Уже выданные токены продолжат работать до истечения срока — их можно отозвать отдельно в списке токенов.'
            : 'Сессия партнёра по этому токену будет немедленно завершена. Партнёр сможет получить новый токен, если его учётные данные не отозваны.'
        }
        confirmText={revoking ? 'Отзыв...' : 'Отозвать'}
        onConfirm={confirmRevoke}
        onCancel={() => !revoking && setRevokeTarget(null)}
      />
    </div>
  );
}

export default AutoparkApiTokens;
